import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../chrome/AppShell';
import useUserOrg from '../../../Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import { useDeviceInviteList } from '../../../DeviceManagement/components/hooks/useDeviceInviteList';
import { useDeviceGroupList } from '../../../DeviceManagement/components/hooks/useDeviceGroupList';
import { useDeviceInvite } from '../../../DeviceManagement/components/hooks/useDeviceInvite';
import { ConfirmDialog, Crumb, Dialog, Field, Select, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { TCard, TEmpty, TFoot, TRow, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import './devices.css';

/*
 * A device group's open invites, with "New invite" in a dialog.
 * The invite list hook has no refetch, so the list remounts by key.
 */

function InviteList({
    orgId,
    groupId,
    hidden,
    onRetry,
    onNew,
    onDelete,
}: {
    orgId: number;
    groupId: number;
    hidden: Set<number>;
    onRetry: () => void;
    onNew: () => void;
    onDelete: (inv: DeviceInvite) => void;
}) {
    const { deviceInvites, isLoading, error } = useDeviceInviteList(orgId, groupId);
    if (error)
        return (
            <TCard>
                <TEmpty message="Couldn’t load invites." actionLabel="Retry" onAction={onRetry} />
            </TCard>
        );
    if (isLoading)
        return (
            <TCard>
                <TSkeleton rows={3} />
            </TCard>
        );
    const rows = (deviceInvites ?? []).filter((i) => !hidden.has(i.id));
    if (rows.length === 0)
        return (
            <TCard>
                <TEmpty message="No open invites for this group." actionLabel="New invite" onAction={onNew} />
            </TCard>
        );
    return (
        <>
            <TCard settle>
                {rows.map((inv, i) => (
                    <TRow
                        key={inv.id}
                        index={i}
                        name={inv.name}
                        meta="Enter this name when registering the device"
                        date=""
                        status={inv.claimed ? 'Used' : <span className="ready">Open</span>}
                        onOpen={() => {}}
                        menuItems={[{ label: 'Delete…', onClick: () => onDelete(inv) }]}
                    />
                ))}
            </TCard>
            <TFoot>{`${rows.length} ${rows.length === 1 ? 'invite' : 'invites'} · each name works once`}</TFoot>
        </>
    );
}

export default function DeviceInvitesV3() {
    const navigate = useNavigate();
    const { groupId } = useParams();
    const gid = +(groupId || 0);
    const { orgId } = useUserOrg();
    const api = useAuthenticatedApi();
    const toast = useToast();

    const [version, setVersion] = useState(0);
    const refetch = useCallback(() => setVersion((v) => v + 1), []);
    const [hidden, setHidden] = useState<Set<number>>(() => new Set());

    /* delete */
    const [deleting, setDeleting] = useState<DeviceInvite | null>(null);
    const doDelete = async (inv: DeviceInvite) => {
        setHidden((s) => new Set(s).add(inv.id));
        const restore = () =>
            setHidden((s) => {
                const next = new Set(s);
                next.delete(inv.id);
                return next;
            });
        try {
            const res = await api.delete(`${orgId}/device-invites/${inv.id}`);
            if (res.status !== 200) throw new Error(String(res.status));
            toast.show(`Deleted invite “${inv.name}”`);
        } catch {
            restore();
            toast.show(`Couldn’t delete “${inv.name}”. Try again.`);
        }
    };

    const [creating, setCreating] = useState(false);

    return (
        <AppShell>
            <div className="dv-page">
                <Crumb label="Devices" onClick={() => navigate('/device-management#groups')} />
                {orgId ? (
                    <InvitesBody
                        orgId={orgId}
                        groupId={gid}
                        creating={creating}
                        setCreating={setCreating}
                        onCreated={(name, toGroup) => {
                            toast.show(
                                toGroup === gid
                                    ? `Invite “${name}” created`
                                    : `Invite “${name}” created in another group`,
                            );
                            if (toGroup === gid) refetch();
                        }}
                        list={
                            <InviteList
                                key={version}
                                orgId={orgId}
                                groupId={gid}
                                hidden={hidden}
                                onRetry={refetch}
                                onNew={() => setCreating(true)}
                                onDelete={setDeleting}
                            />
                        }
                        onOpenGroup={() => navigate(`/device-management/device-groups/${groupId}/device-keys`)}
                    />
                ) : (
                    <TCard>
                        <TSkeleton rows={3} />
                    </TCard>
                )}
            </div>
            <ConfirmDialog
                open={deleting !== null}
                title={`Delete invite “${deleting?.name ?? ''}”?`}
                body="A device can no longer register with this name. This can’t be undone."
                actionLabel="Delete invite"
                onConfirm={() => deleting && doDelete(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AppShell>
    );
}

/* Split out so the group-list hook only runs once an org is known. */
function InvitesBody({
    orgId,
    groupId,
    creating,
    setCreating,
    onCreated,
    list,
    onOpenGroup,
}: {
    orgId: number;
    groupId: number;
    creating: boolean;
    setCreating: (v: boolean) => void;
    onCreated: (name: string, groupId: number) => void;
    list: React.ReactNode;
    onOpenGroup: () => void;
}) {
    const { deviceGroups, isLoading: groupsLoading, error: groupsError } = useDeviceGroupList(orgId);
    const { createDeviceInvite, error: serverError, setError } = useDeviceInvite(orgId);
    const group = deviceGroups.find((g) => g.id === groupId);

    const [name, setName] = useState('');
    const [target, setTarget] = useState(String(groupId));
    const [nameError, setNameError] = useState('');
    const [groupError, setGroupError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const busy = useRef(false);

    const close = useCallback(() => {
        if (busy.current) return;
        setCreating(false);
        setName('');
        setTarget(String(groupId));
        setNameError('');
        setGroupError('');
        setSaveError('');
        setError(null);
    }, [groupId, setCreating, setError]);

    const submit = async () => {
        if (busy.current) return;
        const trimmed = name.trim();
        const to = Number(target);
        let bad = false;
        if (!trimmed) {
            setNameError('Give the invite a name.');
            bad = true;
        }
        if (!to) {
            setGroupError('Choose a group.');
            bad = true;
        }
        if (bad) return;
        busy.current = true;
        setSaving(true);
        setSaveError('');
        let ok: unknown;
        try {
            ok = await createDeviceInvite({ name: trimmed, groupId: to });
        } catch {
            ok = undefined;
        }
        busy.current = false;
        setSaving(false);
        if (ok) {
            close();
            onCreated(trimmed, to);
        } else {
            setSaveError('Couldn’t create the invite. Try again.');
        }
    };

    return (
        <>
            <PageHeadActions
                title={group ? `${group.name} invites` : 'Device invites'}
                sub="A device registers by entering an invite’s name. Each name works once."
                actions={
                    <>
                        <button className="tb-btn" onClick={onOpenGroup}>
                            Group devices
                        </button>
                        <button className="tb-new" onClick={() => setCreating(true)}>
                            New invite
                        </button>
                    </>
                }
            />
            {list}
            <Dialog
                open={creating}
                title="New device invite"
                onClose={close}
                onSubmit={submit}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={close} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={saving || groupsLoading}>
                            {saving ? 'Creating…' : 'Create invite'}
                        </button>
                    </>
                }
            >
                <Field label="Invite name" help="Enter this on the device when you register it." error={nameError}>
                    <TextInput
                        autoFocus
                        value={name}
                        disabled={saving}
                        aria-invalid={!!nameError}
                        onChange={(e) => {
                            setName(e.target.value);
                            setNameError('');
                        }}
                    />
                </Field>
                <Field
                    label="Device group"
                    error={groupError || (groupsError ? 'Couldn’t load groups.' : undefined)}
                >
                    <Select
                        value={target}
                        disabled={saving || groupsLoading}
                        aria-invalid={!!groupError}
                        onChange={(e) => {
                            setTarget(e.target.value);
                            setGroupError('');
                        }}
                    >
                        <option value="0">{groupsLoading ? 'Loading groups…' : 'Choose a group'}</option>
                        {deviceGroups.map((g) => (
                            <option key={g.id} value={String(g.id)}>
                                {g.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                {(serverError || saveError) && (
                    <p className="banner dv-note err" role="alert" style={{ margin: '12px 0 0' }}>
                        {serverError || saveError}
                    </p>
                )}
            </Dialog>
        </>
    );
}
